/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { ConfigService } from 'src/app/service/config.service';
import { RoutingService } from 'src/app/service/routing.service';
import { ParamsBuilder } from '../../utils/params-builder';
import { EntityInfoService } from '../metadata-api/entity-info.service';
import { SentimentService } from './sentiment.service';
import { QuantApiService } from '../quant-api/quant-api.service';

/**
 * Multi-API orchestrators relocated verbatim from `yukkapi.service.ts` (the last
 * §D teardown step): `treemap` (market-board hierarchy + sentiment fan-out, or
 * entity relations), `relations` (entity → related entities + sentiment), and
 * `peergroupComparison`. Metadata/sentiment sub-fetches delegate to the canonical
 * `EntityInfoService` / `SentimentService`; only the quant `peergroup` fetch has
 * no existing home and lives here. Orchestration order + response assembly are
 * preserved byte-for-byte — the nested treemap/relations structures are built by
 * positional index, so ordering is load-bearing.
 */
@Injectable({
  providedIn: 'root',
})
export class CompositeDataService {
  constructor(
    private entityInfo: EntityInfoService,
    private sentiment: SentimentService,
    private quantApi: QuantApiService,
    private config: ConfigService,
    private routing: RoutingService,
  ) {}

  /**
   * treemap data for the market board and the company page
   */
  treemap(iparams): Observable<any> {
    if (iparams && iparams.type && iparams.type === 'hierarchy') {
      if (iparams.id && ['industry', 'country', 'index'].includes(iparams.id)) {
        return this.entityInfo.getHierarchyInfo(iparams.id, iparams);
      } else {
        return this.entityInfo.getHierarchyInfo('icb', iparams).pipe(
          mergeMap((resArg) => {
            const res = JSON.parse(JSON.stringify(resArg));
            const requestSentimentList = [];
            const industries = [];
            res.treemap.children.forEach((industry) => {
              industries.push(industry.entity.compound_key);
              const supersectors = [];
              industry.children.forEach((supersector) => {
                supersectors.push(supersector.entity.compound_key);
              });
              // Per-item sentiment is best-effort — a single failed industry or
              // supersector fetch must not blank the whole board.
              requestSentimentList.push(
                this.sentiment
                  .getSentimentItemized(supersectors, iparams)
                  .pipe(catchError(() => of({}))),
              );
            });
            requestSentimentList.unshift(
              this.sentiment
                .getSentimentItemized(industries, iparams)
                .pipe(catchError(() => of({}))),
            );
            return forkJoin(requestSentimentList).pipe(
              map((resArg2) => {
                const res2 = JSON.parse(JSON.stringify(resArg2));
                const responseObject = Object.assign({}, res);
                responseObject.treemap.children.forEach((industry, index) => {
                  responseObject.treemap.children[index] = Object.assign(
                    {},
                    responseObject.treemap.children[index],
                    res2[0][industry.entity.compound_key],
                  );
                  responseObject.treemap.children[index].children.forEach(
                    (supersector, index2) => {
                      responseObject.treemap.children[index].children[index2] =
                        Object.assign(
                          {},
                          responseObject.treemap.children[index].children[
                            index2
                          ],
                          res2[1 + index][supersector.entity.compound_key],
                        );
                    },
                  );
                });
                return responseObject;
              }),
            );
          }),
        );
      }
    } else if (iparams && iparams.type && iparams.type === 'company') {
      // The mosaic renders only the flattened child entities — skip the parent
      // entity + per-type aggregate sentiment it never displays. `stock_index`
      // is omitted: it resolves to the company's own listed securities (ISINs),
      // which aren't meaningful related-entity tiles.
      return this.relations(
        iparams,
        ['country', 'industry', 'person', 'sector', 'subsector', 'supersector'],
        { withParentEntity: false, withAggregateSentiment: false },
      ).pipe(
        map((resArg) => {
          const res = JSON.parse(JSON.stringify(resArg));
          return {
            treemap: Object.assign({}, res, {
              children: Object.values(res.relations),
            }),
          };
        }),
      );
    } else if (iparams && iparams.type) {
      return this.relations(iparams, ['company'], {
        withParentEntity: false,
        withAggregateSentiment: false,
      }).pipe(
        map((resArg) => {
          const res = JSON.parse(JSON.stringify(resArg));
          return {
            treemap: Object.assign({}, res, {
              children: res.relations.company.children,
            }),
          };
        }),
      );
    } else {
      return of({
        treemap: {
          children: [],
        },
      });
    }
  }

  /**
   * Related entities grouped by type, each decorated with per-entity sentiment.
   *
   * `opts` lets a caller fetch only what it renders:
   *  - `withParentEntity`       include the parent entity object (news-treemap header)
   *  - `withAggregateSentiment` include each type's aggregate sentiment (news-treemap tiles)
   *
   * The entity-mosaic treemap renders only the flattened child entities, so it
   * passes both `false` and skips the parent + per-type aggregate requests it
   * never displays. Every leaf falls back to empty on failure so one bad request
   * cannot collapse the fan-out.
   */
  relations(
    iparams,
    types,
    opts: { withParentEntity?: boolean; withAggregateSentiment?: boolean } = {},
  ): Observable<any> {
    if (!iparams.type || !iparams.id) {
      return of({ entity: {}, relations: {} });
    }
    const { withParentEntity = true, withAggregateSentiment = true } = opts;
    const entityType = iparams['type'];
    const entityId = iparams['id'];
    const compoundKey = `${entityType}:${entityId}`;

    const parent$ = withParentEntity
      ? this.entityInfo
          .getEntitiesInfo([compoundKey], 'array')
          .pipe(catchError(() => of([])))
      : of([]);
    const related$ = types.length
      ? forkJoin(
          types.map((type) =>
            this.entityInfo
              .getRelatedEntitiesInfo(entityType, entityId, type)
              .pipe(catchError(() => of([]))),
          ),
        )
      : of([]);

    return forkJoin({ parent: parent$, related: related$ }).pipe(
      mergeMap(({ parent, related }: { parent: any; related: any }) => {
        // One itemized-sentiment call for every related entity across all
        // types: the response is keyed by compound_key, so splitting it per
        // type is pointless network chatter.
        const allKeys = related.flat().map((el) => el.compound_key);
        const itemized$ = allKeys.length
          ? this.sentiment
              .getSentimentItemized(allKeys, iparams)
              .pipe(catchError(() => of({})))
          : of({});
        const aggregate$ =
          withAggregateSentiment && related.length
            ? forkJoin(
                related.map((entities) =>
                  entities.length
                    ? this.sentiment
                        .getPortfolioSentiment(
                          entities.map((el) => el.compound_key),
                          iparams,
                          false,
                        )
                        .pipe(catchError(() => of({})))
                    : of({}),
                ),
              )
            : of([]);

        return forkJoin({ itemized: itemized$, aggregate: aggregate$ }).pipe(
          map(({ itemized, aggregate }) => {
            // Clone before assembly: metadata GETs may be served from cache and
            // downstream views mutate the assembled nodes.
            const relatedC = JSON.parse(JSON.stringify(related));
            const sentimentByKey = JSON.parse(JSON.stringify(itemized));
            const aggregateC = JSON.parse(JSON.stringify(aggregate));
            const parentC = JSON.parse(JSON.stringify(parent));

            const relations = {};
            types.forEach((type, index) => {
              const label = type[0].toUpperCase() + type.slice(1);
              relations[type] = Object.assign(
                {
                  children: relatedC[index].map((entity) =>
                    Object.assign(
                      { entity },
                      sentimentByKey[entity.compound_key],
                    ),
                  ),
                  name: label,
                  entity: { alpha_id: type, name: label, type: 'relations' },
                },
                withAggregateSentiment ? aggregateC[index]?.['sentiment'] : {},
              );
            });
            return { entity: withParentEntity ? parentC[0] : {}, relations };
          }),
        );
      }),
    );
  }

  /**
   * peergroup comparison for companies including scores
   */
  peergroupComparison(iparams): Observable<any> {
    if (iparams && iparams.id && iparams.type && iparams.type === 'company') {
      return this.getPeergroup(iparams).pipe(
        mergeMap((resArg) => {
          const res = JSON.parse(JSON.stringify(resArg));
          const content = res.companies.map((el) => el.compound_key);
          content.push(iparams.type + ':' + iparams.id);
          return this.entityInfo.getEntitiesInfo(content, 'array').pipe(
            mergeMap((resArg2: any[]) => {
              return this.sentiment.getSentimentItemized(content, iparams).pipe(
                map((resArg3) => {
                  return resArg2.map((el) => {
                    return Object.assign({}, el, resArg3[el.compound_key]);
                  });
                }),
              );
            }),
          );
        }),
      );
    }

    if (iparams && iparams.id && iparams.type === 'country') {
      return this.entityInfo
        .getRelatedEntitiesInfo('country', iparams.id, 'company')
        .pipe(
          map((entities: any) => {
            const id = String(iparams.id).toLowerCase();
            return (Array.isArray(entities) ? entities : [])
              .filter((e) => {
                const home = e?.description?.Country?.entity?.alpha_id;
                return typeof home === 'string' && home.toLowerCase() === id;
              })
              .slice(0, 12)
              .map((e) => ({
                type: 'company',
                name: e.name,
                alpha_id:
                  e.alpha_id ??
                  String(e.compound_key ?? '')
                    .split(':')
                    .slice(1)
                    .join(':'),
                compound_key: e.compound_key,
              }));
          }),
          catchError(() => of([])),
        );
    }

    return of([]);
  }

  /**
   * peergroup companies (quant `misc/company/{id}/peergroup`)
   */
  private getPeergroup(iparams): Observable<any> {
    const params = ParamsBuilder.build(
      iparams,
      ['period', ...ParamsBuilder.PARAMS_FOR_ALL_REQUESTS],
      this.config.appConfig.routing.apiproj,
      { isReader: this.routing.isReader() || this.routing.isStoryReader() },
    );
    return this.quantApi.get(`misc/company/${iparams.id}/peergroup`, params);
  }
}
